import { useEffect, useRef, useState } from 'react';
import { dateTime } from '@wyocspay/ui';
import { api, type StatusView } from './api.js';
import type { Strings } from './i18n.js';
import { ResultView, type ResultPhase } from './Result.js';
import { Advertisement } from './Advertisement.js';

/**
 * The customer Result step: polls the server (the only authority on payment
 * outcome) and renders the shared animated ResultView. A redirect or success
 * page is never proof of payment; SUCCEEDED comes only from a verified event.
 *
 * Once the payment is confirmed the screen counts down and, at zero, asks the
 * server to close the link and tries to close the tab. A paid link must not
 * keep working: after this the URL lands on a "payment complete, link closed"
 * page rather than an interactive one.
 */
export function StatusScreen({
  token, t, locale, merchant, merchantLogo, paymentId, onRetry,
}: {
  token: string; t: Strings; locale: string; merchant: string; merchantLogo?: string | null; paymentId?: string | null; onRetry: () => void;
}) {
  const [status, setStatus] = useState<StatusView | null>(null);
  // Stop implying imminent completion after this long without a verified
  // outcome, and offer a manual re-check instead of spinning forever.
  const [timedOut, setTimedOut] = useState(false);
  // Bumped by "Check again" to restart the poll (and its 5-minute deadline).
  const [round, setRound] = useState(0);
  // Seconds until the page closes itself once payment is confirmed.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  // True once the link has been closed and the page is a dead end.
  const [closed, setClosed] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const deadline = Date.now() + 5 * 60 * 1000;
    setTimedOut(false);
    async function poll(): Promise<void> {
      if (cancelled) return;
      try {
        const next = await api.status(token);
        if (cancelled) return;
        setStatus(next);
        if (!next.pending) return;
      } catch { /* transient; keep polling */ }
      if (Date.now() >= deadline) { if (!cancelled) setTimedOut(true); return; }
      attempt += 1;
      timer.current = window.setTimeout(() => void poll(), Math.min(1000 + attempt * 400, 5000));
    }
    void poll();
    return () => { cancelled = true; if (timer.current) window.clearTimeout(timer.current); };
  }, [token, round]);

  const phase: ResultPhase = !status ? 'processing' : status.pending ? 'processing' : status.confirmed ? 'ok' : 'declined';

  // Confirmed: count down 20 seconds, then close the link and the tab.
  useEffect(() => {
    if (phase !== 'ok' || closed) return;
    let left = 20;
    setSecondsLeft(left);
    const id = window.setInterval(() => {
      left -= 1;
      setSecondsLeft(left);
      if (left <= 0) {
        window.clearInterval(id);
        void api.close(token);
        try { window.close(); } catch { /* browsers block closing tabs they did not open */ }
        setClosed(true);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, closed, token]);

  if (closed) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div
            aria-hidden
            style={{
              width: 56, height: 56, margin: '0 auto 18px', borderRadius: '50%',
              background: '#e8f4ec', color: '#2d7a4a', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 30,
            }}
          >
            ✓
          </div>
          <h1 style={{ margin: 0, fontSize: 20, color: '#26313a' }}>Payment complete</h1>
          <p style={{ marginTop: 10, fontSize: 14, color: '#6d7880', lineHeight: 1.5 }}>
            Your payment to {merchant} went through. This link is now closed - you can safely close this tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ResultView
        phase={phase}
        authorized
        amount={status?.amount ?? null}
        currency={status?.currency ?? 'usd'}
        merchant={merchant}
        merchantLogo={merchantLogo}
        paymentId={paymentId}
        message={status?.failure?.message ?? null}
        declineCode={status?.failure?.decline_code ?? status?.failure?.code ?? null}
        receiptAt={status?.paid_at ? dateTime(status.paid_at, locale) : null}
        timedOut={phase === 'processing' && timedOut}
        onRefresh={() => { setTimedOut(false); setRound((r) => r + 1); }}
        // The admin-booked advertising slot, in the result's right-hand column;
        // self-hides when nothing is booked. Only on a settled result, never over
        // a still-processing screen that asks the customer to wait.
        aside={phase === 'ok' ? <Advertisement token={token} t={t} /> : null}
        t={t}
        locale={locale}
        onRetry={onRetry}
        onChangeMethod={onRetry}
      />
      {phase === 'ok' && secondsLeft !== null ? (
        <div style={{ textAlign: 'center', color: '#8a959c', fontSize: 13, marginTop: 14 }}>
          This page will close automatically in {secondsLeft}s.
        </div>
      ) : null}
    </>
  );
}
